'use client';

import { useCallback, useEffect, useState } from 'react';

import { Minus, Plus, RotateCcw, RotateCw } from 'lucide-react';
import CropperLib from 'react-easy-crop';
import type { Area, CropperProps } from 'react-easy-crop';

import { Button } from '../atoms/button';
import { Checkbox } from '../atoms/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../atoms/dialog';
import { cn } from '../utils';

// react-easy-crop uses a class component incompatible with React 19's stricter JSX types.
// Partial because the class has defaultProps for minZoom, maxZoom, zoomSpeed, classes, etc.
const Cropper = CropperLib as unknown as React.FC<
  Partial<CropperProps> &
    Pick<CropperProps, 'image' | 'crop' | 'onCropChange' | 'onCropComplete'>
>;

// ─── Constants ──────────────────────────────────────────────────────────────

const MIN_ZOOM = 0.95;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.01;
const ZOOM_BUTTON_STEP = 0.3;
const ROTATION_STEP = 90;

// ─── Crop image utility (from legacy cropImage.ts) ──────────────────────────

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = (rotation * Math.PI) / 180;
  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export async function createCroppedImage(
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
): Promise<string> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to create canvas context');

  const rotRad = (rotation * Math.PI) / 180;
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');
  if (!croppedCtx) throw new Error('Failed to create canvas context');

  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob(
      blob => {
        if (blob) resolve(URL.createObjectURL(blob));
        else reject(new Error('Failed to generate cropped image'));
      },
      'image/jpeg',
      0.92,
    );
  });
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ImageCropConfig {
  aspectRatio: number;
  allowPreserveImageAspectRatio?: boolean;
  cropShape?: 'rect' | 'round';
  /** How the image fits inside the cropper container.
   *  Defaults to `'horizontal-cover'`. Use `'contain'` to
   *  fit the entire image inside the container so the user
   *  can see the whole photo while choosing the crop area
   *  (and zoom out to small enough that the picture is
   *  smaller than the crop frame). */
  objectFit?: 'contain' | 'horizontal-cover' | 'vertical-cover' | 'cover';
}

export interface ImageCropperDialogProps {
  /** Object URL of the image to crop */
  image: string | undefined;
  /** Aspect ratio for the crop area */
  aspectRatio: number;
  /** Natural aspect ratio of the image (for preserve toggle) */
  naturalAspectRatio?: number;
  /** Allow user to toggle "preserve original aspect ratio" */
  allowPreserveImageAspectRatio?: boolean;
  /** Round or rect crop shape */
  cropShape?: 'rect' | 'round';
  /** How the image fits inside the crop container. Defaults to 'horizontal-cover'. */
  objectFit?: 'contain' | 'horizontal-cover' | 'vertical-cover' | 'cover';
  /** Called with (error, croppedObjectUrl) — matches legacy callback signature */
  onCropped: (error: Error | undefined, result: string | undefined) => void;
  /** Called when the dialog is dismissed without cropping */
  onDismiss: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ImageCropperDialog({
  image,
  aspectRatio,
  naturalAspectRatio,
  allowPreserveImageAspectRatio,
  cropShape = 'rect',
  objectFit = 'contain',
  onCropped,
  onDismiss,
}: ImageCropperDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [cropAreaPixels, setCropAreaPixels] = useState<Area | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [preserveAspectRatio, setPreserveAspectRatio] = useState(false);

  // Reset all state when a new image is opened (or dialog closes)
  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(0.95);
    setRotation(0);
    setCropAreaPixels(null);
    setIsCropping(false);
    setPreserveAspectRatio(false);
  }, [image]);

  const effectiveAspectRatio =
    preserveAspectRatio && naturalAspectRatio != null
      ? naturalAspectRatio
      : aspectRatio;

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      if (
        isNaN(croppedAreaPixels.x) ||
        isNaN(croppedAreaPixels.y) ||
        isNaN(croppedAreaPixels.width) ||
        isNaN(croppedAreaPixels.height)
      ) {
        return;
      }
      setCropAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleTogglePreserve = (checked: boolean) => {
    setPreserveAspectRatio(checked);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleRotate = (direction: 'cw' | 'ccw') => {
    setRotation(prev =>
      direction === 'cw' ? prev + ROTATION_STEP : prev - ROTATION_STEP,
    );
  };

  const handleCrop = useCallback(async () => {
    if (!image || !cropAreaPixels) return;
    setIsCropping(true);
    try {
      const result = await createCroppedImage(image, cropAreaPixels, rotation);
      onCropped(undefined, result);
    } catch (error) {
      console.error('Failed to crop image:', error);
      onCropped(
        error instanceof Error ? error : new Error('Crop failed'),
        undefined,
      );
    } finally {
      setIsCropping(false);
    }
  }, [cropAreaPixels, image, onCropped, rotation]);

  return (
    <Dialog
      open={image != null}
      onOpenChange={open => {
        if (!open) onDismiss();
      }}
    >
      <DialogContent
        className={cn(
          'bg-surface-1 text-content-1 gap-0 overflow-hidden border-none p-0',
          // Full-bleed on mobile, wider on desktop for best crop experience
          'w-[calc(100vw-0.5rem)] max-w-none rounded-xl',
          'sm:w-[calc(100vw-2rem)] sm:max-w-none md:max-w-4xl lg:max-w-5xl',
          // Disable scale animation — react-easy-crop uses getBoundingClientRect
          // on mount which returns the animated (95%) size, and its internal
          // ResizeObserver skips the first resize so crop is never recalculated.
          'data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100',
          // Force the cropper above other modals on the page.
          // The bio-route visitor dialogs (login / my account /
          // tipping / contact-card preview) all use `z-[101]`
          // to overlay the bio chrome; the cropper has to sit
          // above them when launched from those modals.
          'z-[200]',
        )}
        overlayClassName="z-[199]"
        showCloseButton={false}
      >
        <DialogTitle className="text-content-1 px-5 py-4 text-center text-base font-semibold">
          Crop Image
        </DialogTitle>
        <DialogDescription className="sr-only">
          Drag and zoom to crop your image
        </DialogDescription>

        {/* Cropper area */}
        {image != null && (
          <>
            <div className="bg-surface-2 relative h-[50vh] w-full md:h-[65vh]">
              <Cropper
                image={image}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={effectiveAspectRatio}
                cropShape={cropShape}
                // When "preserve original aspect ratio" is on, the crop frame
                // becomes the image's natural ratio, so the entire picture must
                // be visible — `contain` fits it whole. The default cover modes
                // fill the frame and clip/zoom the image, which is exactly the
                // "image stays zoomed, not fully shown" bug.
                objectFit={preserveAspectRatio ? 'contain' : objectFit}
                showGrid={true}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: { background: '#000' },
                  cropAreaStyle: {
                    border: '2px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
                  },
                }}
              />
            </div>

            {/* Controls bar: zoom slider + rotation buttons */}
            <div className="flex items-center gap-2 px-5 py-3">
              {/* Zoom out */}
              <button
                type="button"
                onClick={() =>
                  setZoom(prev => Math.max(MIN_ZOOM, prev - ZOOM_BUTTON_STEP))
                }
                className="text-content-3 hover:bg-raise/10 hover:text-content-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors"
                aria-label="Zoom out"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>

              {/* Zoom slider */}
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={ZOOM_STEP}
                value={zoom}
                onChange={event => setZoom(Number(event.target.value))}
                className="bg-surface-3 accent-brand [&::-webkit-slider-thumb]:bg-brand h-1 flex-1 cursor-pointer appearance-none rounded-full [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full"
              />

              {/* Zoom in */}
              <button
                type="button"
                onClick={() =>
                  setZoom(prev => Math.min(MAX_ZOOM, prev + ZOOM_BUTTON_STEP))
                }
                className="text-content-3 hover:bg-raise/10 hover:text-content-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors"
                aria-label="Zoom in"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>

              {/* Divider */}
              <div className="bg-surface-3 mx-1 h-5 w-px" />

              {/* Rotate CCW */}
              <button
                type="button"
                onClick={() => handleRotate('ccw')}
                className="text-content-3 hover:bg-raise/10 hover:text-content-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors"
                aria-label="Rotate left"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>

              {/* Rotate CW */}
              <button
                type="button"
                onClick={() => handleRotate('cw')}
                className="text-content-3 hover:bg-raise/10 hover:text-content-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors"
                aria-label="Rotate right"
              >
                <RotateCw className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Preserve aspect ratio checkbox */}
            {allowPreserveImageAspectRatio && naturalAspectRatio != null && (
              <div className="flex items-center justify-center gap-2 px-5 pb-2">
                <Checkbox
                  id="preserve-aspect-ratio"
                  checked={preserveAspectRatio}
                  onCheckedChange={checked =>
                    handleTogglePreserve(checked === true)
                  }
                  className="border-content-4 data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=checked]:text-content-on-brand"
                />
                <label
                  htmlFor="preserve-aspect-ratio"
                  className="text-content-2 cursor-pointer text-xs"
                >
                  Use original image aspect ratio
                </label>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 px-5 pb-5">
              <Button
                type="button"
                variant="outline"
                onClick={onDismiss}
                className="border-surface-3 text-content-1 hover:bg-surface-2 flex-1 rounded-full bg-transparent text-sm"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCrop}
                disabled={!cropAreaPixels || isCropping}
                className="bg-brand hover:bg-brand-hover text-content-on-brand flex-1 rounded-full text-sm font-semibold hover:opacity-90 disabled:opacity-40"
              >
                {isCropping ? 'Cropping...' : 'Crop Image'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
